import NextApp, { AppContext } from 'next/app';
import { IncomingMessage } from 'http';
import { useEffect, useState } from 'react';

import { BspProvider } from '@eventbrite/bsp-sdk/react';
import { Stage, stageFromString } from '@eventbrite/webapp-constants';
import { ConsentManagerScripts } from '@eventbrite/webapp-data';

import { useBspConfig } from '@/bsp/config';
import { Wrapper } from '@/components/_shared/Wrapper';
import {
  getListingsDesktopTwoRailsFromExperiments,
  getListingsDesktopTwoRailsFromExperimentsSSR,
} from '@/utils/listingsDesktopTwoRails';
import { IntlProviderWrapper } from '@/providers/IntlProviders';
import { QueryClientProvider } from '@/providers/QueryClientProvider';
import { EVENT_STATUS, ListingsAppProps } from '@/types';
import { getDefaultDomainUrl, getDomainUrl } from '@/utils/utils';

import { buildListingsUserCustom, getStatsigEnvFromStage, useInitStatsig } from '../../common/statsig';
import { DatadogInit } from '../../common/monitoring/datadogrum';

import '@/styles/globals.css';
import '@/styles/overrides.css';
// be_grotesk Founders Grotesk preview override (activated via ?be_grotesk=true)
import '@eventbrite/webapp-fonts/fonts.css';

export default function App({ Component, pageProps, serverUrl, stage, statsigClientKey }: ListingsAppProps) {
  const statsigCustom = pageProps.context
    ? buildListingsUserCustom(pageProps.context, pageProps.userIsAuthenticated)
    : undefined;
  const deactivateStatsig =
    pageProps.context?.basicInfo?.status === EVENT_STATUS.ENDED ||
    pageProps.context?.basicInfo?.status === EVENT_STATUS.COMPLETED ||
    pageProps.protectedEvent?.isEnded === true;

  useInitStatsig({
    clientKey: statsigClientKey,
    stableId: pageProps.stableId,
    custom: statsigCustom,
    environmentTier: getStatsigEnvFromStage(stage),
    deactivateSdk: deactivateStatsig,
  });

  const bspConfig = useBspConfig();
  const { locale = 'en-US', messages = {} } = pageProps;

  // Use SSR-safe version for initial render (no URL override checks)
  const experimentsData = 'experiments' in pageProps ? pageProps.experiments : undefined;
  const [listingsDesktopTwoRails, setListingsDesktopTwoRails] = useState(
    getListingsDesktopTwoRailsFromExperimentsSSR(experimentsData),
  );

  // Handle client-side URL overrides after hydration
  useEffect(() => {
    const clientSideValue = getListingsDesktopTwoRailsFromExperiments(experimentsData);
    setListingsDesktopTwoRails(clientSideValue);
  }, [experimentsData]);

  return (
    <BspProvider config={bspConfig}>
      <QueryClientProvider>
        <IntlProviderWrapper messages={messages} locale={locale}>
          <Wrapper
            serverUrl={serverUrl}
            stage={stage}
            locale={locale}
            listingsDesktopTwoRails={listingsDesktopTwoRails}
          >
            <DatadogInit stage={stage} />
            <ConsentManagerScripts />
            <Component {...pageProps} stage={stage} />
          </Wrapper>
        </IntlProviderWrapper>
      </QueryClientProvider>
    </BspProvider>
  );
}

App.getInitialProps = async (context: AppContext) => {
  const req = context.ctx.req as IncomingMessage | undefined;
  const appProps = await NextApp.getInitialProps(context);

  const serverUrl = req ? getDomainUrl(req) : getDefaultDomainUrl();

  // Get stage from environment variable
  const stage = stageFromString(process.env.STAGE || Stage.TEST);

  const statsigClientKey = process.env.STATSIG_CLIENT_KEY ?? '';

  return {
    ...appProps,
    serverUrl,
    stage,
    statsigClientKey,
  };
};
